import { Component, OnInit } from '@angular/core';
import {
  moveItemInArray,
  transferArrayItem,
  CdkDragStart,
} from '@angular/cdk/drag-drop';

import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute } from '@angular/router';
import { FarmService } from '../../services/farm.service';
import { BlockService } from '../../services/block.service';
import { GroupService } from '../../services/group.service';
import { DialogService } from '../../services/dialog.service';

import {  cloneDeep } from 'lodash';
import { LoggingService } from 'src/app/services/logging.service';
import { CognitoService } from 'src/app/services/cognito.service';
import { isUsableId } from '../../utils/valid-id';
import {
  YieldSourceBanner,
  yieldSourceBanner,
} from '../../utils/yield-source-banner';

/**
 * SRA003-408 (interim, FE-only): hide predicted scenarios that need more than
 * `maxHours` working hours/day, keyed on
 * `block_agronomic_results.hours_per_day_total_pred` (MAX semantics — hide
 * scenarios needing MORE than the threshold).
 *
 * - The Standard Practice row (`standard === true`) is ALWAYS kept — it is not
 *   a sweep scenario.
 * - Zero-result fallback: if no predicted scenario is within the threshold,
 *   return all predicted scenarios (the table is never emptied; the caller
 *   surfaces a "showing all" note).
 * - The BE flags optimal scenarios via `is_optimal` (SRA003-469), so "all"
 *   means all RETURNED scenarios, not a hard 11.
 *
 * Pure function — no component state — so it can be unit-tested directly.
 */
export function filterScenariosByMaxHours(
  results: any[],
  maxHours: number,
): any[] {
  if (!Array.isArray(results)) return [];
  const standard = results.filter((r) => r?.standard);
  const predicted = results.filter((r) => !r?.standard);
  const within = predicted.filter(
    (r) =>
      (r?.block_agronomic_results?.hours_per_day_total_pred ?? Infinity) <=
      maxHours,
  );
  return [...standard, ...(within.length > 0 ? within : predicted)];
}

/**
 * SRA003-469: scenario optimality is now the BE's `is_optimal` flag on the /v2
 * (SAM) calc block_results — the single source of truth. is_optimal already
 * encodes the canonical min-net-benefit threshold + flow cutoff the BE applies,
 * so the FE no longer recomputes any of it (the old $30 / remove-non-optimal
 * copy is gone — SRA003-468's recompute is superseded).
 *
 * `applyOptimalFilter` is the state of the visible "Show optimal scenarios
 * only" toggle (the -468 control, retained): ON → the predicted set is gated to
 * is_optimal scenarios; OFF → the full sweep renders, so the hours filter has
 * visible work. Sorted by grower net benefit desc so the best is starred first.
 *
 * Defensive: a scenario missing is_optimal (older BE / fallback) is SHOWN, not
 * hidden — the flag is a no-hard-dependency optimisation, never a gate that can
 * empty the table on a schema gap.
 *
 * Pure function — no component state — so it can be unit-tested directly.
 */
export function filterScenariosByOptimal(
  results: any[],
  applyOptimalFilter: boolean,
): any[] {
  if (!Array.isArray(results)) return [];
  const gated = applyOptimalFilter
    ? // is_optimal !== false: true is kept, and a missing flag is kept too
      results.filter((item) => item?.is_optimal !== false)
    : [...results];
  return gated.sort(
    // best net benefit first (drives the ⭐ "optimal" ranking in the table)
    (a: any, b: any) =>
      (b?.block_economic_results_grower?.net_benefit ?? 0) -
      (a?.block_economic_results_grower?.net_benefit ?? 0),
  );
}

@Component({
    selector: 'app-group-grower-block',
    templateUrl: './group-grower-block.component.html',
    styleUrls: ['./group-grower-block.component.css'],
    providers: [FarmService, BlockService, GroupService],
    standalone: false
})
export class GroupGrowerBlockComponent implements OnInit {
  representative_areas: any = [];
  is_processing_calculations: boolean = false;
  group_id: string;
  cognito_id: string;
  allowance_open: boolean = false;
  group: string;
  farm: any;
  block: any = {};
  block_allowance: any = {};
  season: string;
  selectedFarm: string;
  growersData: any = [];
  blocksData: any = [];
  dataPost: any = {};
  weight_percentage: number = 0;


  isGrower?: boolean;
  
  availableFarms: any = [];
  availableBlocks: any = [];
  assignedBlocks: any = [];
  availableSeasons: any = [];
  dragging: boolean;
  dialog_open: boolean = false;
  average_dialog_open: boolean = false;
  block_results: any[] = [];
  /**
   * SRA003-408 (interim): per-calculation "Max working hours/day" threshold.
   * MAX semantics — predicted scenarios needing more than this are hidden.
   * Default 10. FE post-filter only; changing it re-filters the already-
   * returned scenarios with no new BE call. (Admin-configurable + per-contractor
   * ownership remains the eventual scope of -408, deferred.)
   */
  maxWorkingHours: number = 10;
  /**
   * SRA003-468/-469: the visible "Show optimal scenarios only" toggle. The
   * optimality trim lives in the BE's `is_optimal` flag (-469); this control is
   * simply whether to apply that BE-owned gate. ON (default) shows only
   * is_optimal scenarios; OFF renders the full sweep so the hours filter does
   * visible work. Reactive — re-gates the already-returned sweep, no BE call.
   */
  showOptimalOnly: boolean = true;
  /** Raw sweep + standard row from the last calc, kept for live re-gates. */
  private rawCalcResults: any[] = [];
  private stdResult: any = null;
  mlModelVersion: string | null = null;
  mlFallbackReason: string | null = null;
  // SRA003-514: explicit yield-source contract from the calc result.
  // yield_source ∈ {ml, legacy}; reason ∈ {no_row_type, ml_unavailable, unsupported_level, null}.
  yieldSource: string | null = null;
  yieldReason: string | null = null;
  total_representative_area: number = 0;
  total_selected_blocks: number = 0;

  avg_net_benefit: number = 0;
  avg_yield_change: number = 0;
  avg_yield_std: number = 0;
  avg_yield_pred: number = 0;
  avg_ccs_std: number = 0;
  avg_ccs_pred: number = 0;
  avg_fan_speed_std: number = 0;
  avg_fan_speed_pred: number = 0;
  avg_ground_speed_std: number = 0;
  avg_ground_speed_pred: number = 0;
  hours_per_day_cutting_std: number = 0;
  hours_per_day_cutting_pred: number = 0;
  hours_per_day_total_std: number = 0;
  hours_per_day_total_pred: number = 0;
  flow_rate_std: number = 0;
  flow_rate_pred: number = 0;
  bin_weight_std: number = 0;
  bin_weight_pred: number = 0;
  total_cost_change_t: number = 0;
  total_cost_change_hr: number = 0;
  total_cost_change_ha: number = 0;
  rankings: any[] = ["","⭐", ...[...Array(26)].map((_, i) => String.fromCharCode(i + 65))]

  payload: any = {};
  payload_save_results: any = {
    group_id: '',
    items: [],
  };

  constructor(
    private farmApi: FarmService,
    private blockApi: BlockService,
    private groupApi: GroupService,
    private dialog: DialogService,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private logging: LoggingService,
    private auth: CognitoService
  ) {
    this.group_id = '';
    this.group = '';
    this.cognito_id = '';
    this.dragging = false;
    this.season = '2022';
    this.selectedFarm = '';
    this.allowance_open = false;

  }

  /**
   * SRA003-514: plain-language banner stating which yield path was used and why,
   * shown at the top of the calc result. Delegates to the shared pure helper
   * (previously a byte-identical copy lived here and in
   * ContractNegotiationGrowerComponent).
   */
  get yieldSourceBanner(): YieldSourceBanner | null {
    return yieldSourceBanner(
      this.yieldSource,
      this.yieldReason,
      this.mlFallbackReason,
    );
  }

  showAllowance(block_number: string): void {
    this.assignedBlocks.forEach((f: any) => {
      if (f.block_number == block_number) {
        this.block_allowance = f.allowance;
        this.block = block_number;
      }
    });
    this.allowance_open = true;
  }

  calculate_average(): void {
    this.average_dialog_open = true;
    this.avg_net_benefit = 0;
    this.avg_yield_change = 0;
    this.avg_yield_std = 0;
    this.avg_yield_pred = 0;
    this.avg_ccs_std = 0;
    this.avg_ccs_pred = 0;
    this.avg_fan_speed_std = 0;
    this.avg_fan_speed_pred = 0;
    this.avg_ground_speed_std = 0;
    this.avg_ground_speed_pred = 0;
    this.hours_per_day_cutting_std = 0;
    this.hours_per_day_cutting_pred = 0;
    this.hours_per_day_total_std = 0;
    this.hours_per_day_total_pred = 0;
    this.flow_rate_std = 0;
    this.flow_rate_pred = 0;
    this.bin_weight_std = 0;
    this.bin_weight_pred = 0;
    this.total_cost_change_ha = 0;
    this.total_cost_change_hr = 0;
    this.total_cost_change_t = 0;

    this.total_selected_blocks = 0;
    this.assignedBlocks.forEach((block: any) => {
      if (Number(block.block_representative_area) > 0 && block.selected_result) {
        this.avg_net_benefit +=
          block.selected_result.block_economic_results_grower.net_benefit
        this.avg_yield_pred +=
          block.selected_result.block_agronomic_results.yield_pred;
        this.avg_fan_speed_std +=
          block.selected_result.block_agronomic_results.fan_speed_std;
        this.avg_fan_speed_pred +=
          block.selected_result.block_agronomic_results.fan_speed_pred;
        this.avg_ground_speed_pred +=
          block.selected_result.block_agronomic_results.ground_speed_pred;
        this.hours_per_day_total_pred +=
          block.selected_result.block_agronomic_results.hours_per_day_total_pred;
        this.flow_rate_pred +=
          block.selected_result.block_agronomic_results.flow_rate_pred;
        this.bin_weight_pred +=
          block.selected_result.block_agronomic_results.bin_weight_pred;
        this.total_cost_change_ha += block.selected_result.block_economic_results_grower.cost_change_ha;
        this.total_cost_change_hr += block.selected_result.block_economic_results_grower.cost_change_hr;
        this.total_cost_change_t += block.selected_result.block_economic_results_grower.cost_change_t;
        this.total_selected_blocks++;
      }
    });
  }

  saveBlockAllowance() {
    this.total_representative_area = 0;


    this.assignedBlocks.forEach((block: any) => {

      if (block.block_number == this.block) {
        block.topping = this.payload.std_topping;
        block.allowance = this.block_allowance;
        block.block_representative_area =
          this.block_allowance.total_repesentative_area;

        if (block.allowance.total_average_allowance_per_tone > 0) {
          this.payload.allowance = block.allowance.total_average_allowance_per_tone;
        }

        // SRA003-496: persist the computed $/t (+ breakdown) to the dedicated
        // (block, season) table so the calc and live-Start read a durable value
        // instead of only this transient request.allowance. Season is derived
        // server-side from the group (group_id) — not sent. Best-effort: a save
        // failure surfaces a toast but does not block the calculation flow.
        // NOTE: this.season ('2022', hardcoded in the constructor) is a separate
        // latent FE bug; it is deliberately NOT sent here (BE owns the season).
        this.blockApi
          .saveAllowance({
            group_id: this.group_id,
            farm_id: this.selectedFarm,
            block_number: this.block,
            total_average_allowance_per_tone:
              this.block_allowance.total_average_allowance_per_tone,
            total_representative_area:
              this.block_allowance.total_repesentative_area,
            delivered_yield_estimate:
              this.block_allowance.delivered_yield_estimate,
            harvest_allowance: this.block_allowance.harvest_allowance,
            harvest_allowance_tonnes:
              this.block_allowance.harvest_allowance_tonnes,
            haulage_allowance: this.block_allowance.haulage_allowance,
            haulage_allowance_tonnes:
              this.block_allowance.haulage_allowance_tonnes,
            night_allowance: this.block_allowance.night_allowance,
            night_allowance_tonnes:
              this.block_allowance.night_allowance_tonnes,
            sunday_allowance: this.block_allowance.sunday_allowance,
            sunday_allowance_tonnes:
              this.block_allowance.sunday_allowance_tonnes,
          })
          .subscribe({
            next: () => {},
            error: ({ error }) => {
              this.toastr.error(
                error?.errorMessage || 'Failed to save allowance.'
              );
            },
          });

        if (this.payload) {

          this.is_processing_calculations = true;
          this.blockApi
            .calculateBlock(this.payload)
            .subscribe({
              next: (s) => {
                this.is_processing_calculations = false;
                let results: any[] = s.body;
                // [].sort()
                let std_result: any = cloneDeep(results[0]);

                // SRA003-469: gate predicted scenarios by the BE `is_optimal`
                // flag (the visible "Show optimal scenarios only" toggle).
                // Raw sweep kept for live re-gates.
                this.rawCalcResults = results;
                this.block_results = filterScenariosByOptimal(
                  results,
                  this.showOptimalOnly,
                );

                this.mlModelVersion = results[0]?.ml_model_version || null;
                this.mlFallbackReason = results[0]?.ml_fallback_reason ?? null;
                // SRA003-514: explicit yield-source + reason (depends on -514 BE contract).
                this.yieldSource = results[0]?.yield_source ?? null;
                this.yieldReason = results[0]?.reason ?? null;
                this.logging.info({block_results: this.block_results})
                // Replace pred results with std
                Object.entries(std_result).forEach(
                  ([key, value]: [string, any]) => {
                    // Skip scalar/null top-level fields (e.g. ml_model_version, correlation_id)
                    if (value === null || typeof value !== 'object') return;
                    Object.entries(value).forEach(([res_key, _]) => {
                      // If property has pred in name, then replace its value with the std version
                      if (res_key.includes('pred')) {
                        let std_key = res_key.replace('pred', 'std');
                        std_result[key][res_key] = std_result[key][std_key];
                      }
                      if (res_key.includes('change')) {
                        std_result[key][res_key] = 0;
                      }

                      if (res_key.includes('net_benefit')) {
                        std_result[key][res_key] = 0;
                      }
                    });
                  }
              );
       
              std_result.standard = true;
              this.stdResult = std_result;

              block.results = this.block_results
              this.block_results = [std_result, ...this.block_results]
              //show dialog
              this.dialog_open = true;
              //update array for block result
              this.representative_areas.push({
                block_number: this.block,
                rep_area: block.block_representative_area,
                selected_result: {},
              });
            }, 
            error: ({ error }) => {
              this.toastr.error(error.errorMessage)
              this.is_processing_calculations = false;
            }
          })
        }
      }
    });

    this.assignedBlocks.forEach((f: any) => {
      this.total_representative_area += parseFloat(f.block_representative_area);
    });

    this.assignedBlocks.forEach((f: any) => {
      f.percent_area =
        f.block_representative_area / this.total_representative_area;
    });

    this.allowance_open = false;
    this.block_allowance = {};


  }

  //remove farm blocks from items
  save_results_remove(block_number: any): void {
    if (this.payload_save_results.items.length == undefined) {
      // theres just one block
      this.payload_save_results.items.splice(0, 1);
    } else {
      //loop through them
      let index = this.payload_save_results.items.findIndex(
        (f: any) => f.block_number == block_number
      );
      this.payload_save_results.items.splice(index, 1);
    }

    // SRA003-472: save_results_add sets group_id; the remove path never did,
    // so the PUT to /v2/contract-negotiation went out with the initial empty
    // group_id → BE 400 and the removal silently failed to persist. Mirror
    // the add path, then validate the identifiers before the PUT and surface
    // a visible error rather than a silent console 400.
    this.payload_save_results.group_id = this.group_id;

    if (!isUsableId(this.group_id) || !isUsableId(this.selectedFarm)) {
      this.toastr.error(
        'Could not remove the block — the group or farm is not selected. Reopen the group and try again.',
      );
      return;
    }

    this.blockApi.save_results(this.payload_save_results).subscribe({
      error: ({ error }) => {
        this.toastr.error(
          error?.errorMessage ||
            'Could not remove the block. Please try again.',
        );
      },
    });
  }

  //add new selected blocks calculation items
  save_results_add(result: any, block_number: any): void {
    this.payload_save_results.group_id = this.group_id;

    this.payload_save_results.items.push({
      farm_id: this.selectedFarm,
      block_number: block_number,
      result: result,
      representative_area: Number(this.total_representative_area),
    });

    this.blockApi.save_results(this.payload_save_results).subscribe((s) => {

    });
  }

  countChangedHandler(
    event: number,
    variableName: string,
    blockNumber: string
  ) {
    this.total_representative_area = 0;
    this.assignedBlocks.forEach((f: any) => {
      if (f.block_number == blockNumber) {
        f[variableName] = event;
      }
    });
    this.assignedBlocks.forEach((f: any) => {
      this.total_representative_area += f[variableName];
    });
  }

  countAllowanceChangedHandler(event: number, variableName: string) {
    this.block_allowance[variableName] = event;
    
    this.calcAllowanceAverage();
  }

  calcAllowanceAverage() {
    let std_yield = this.block_allowance.delivered_yield_estimate;
    if (std_yield > 0) {
      let total_harvest_allowance = (this.block_allowance.harvest_allowance * this.block_allowance.harvest_allowance_tonnes);
      let total_haulage_allowance = (this.block_allowance.haulage_allowance * this.block_allowance.haulage_allowance_tonnes);
      let total_night_allowance = (this.block_allowance.night_allowance * this.block_allowance.night_allowance_tonnes);
      let total_sunday_allowance = (this.block_allowance.sunday_allowance * this.block_allowance.sunday_allowance_tonnes);

      this.logging.info([total_harvest_allowance,total_haulage_allowance, total_night_allowance,total_sunday_allowance  ])
      this.block_allowance.total_average_allowance_per_tone =
          (total_harvest_allowance +
            total_haulage_allowance+
            total_night_allowance +
            total_sunday_allowance) /
            std_yield;
    } else {
      this.block_allowance.total_average_allowance_per_tone = 0;
    }
  }

  loadGrowers(): any {
    ``;
    this.growersData = [];
    // SRA003-454: group_id is route-sourced; without it, skip rather than
    // fire GET /group/undefined/growers (404).
    if (!isUsableId(this.group_id)) {
      return;
    }
    this.groupApi.list_growers_in_group(this.group_id).subscribe((l) => {
      if (l.body) {
        this.growersData = l.body.growers;
        this.availableFarms = this.growersData.filter(
          (obj: any) => obj.cognito_id == this.cognito_id
        )[0].farms;
      }
    });
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      this.group = params['name'];
      this.group_id = params['group_id'];
      this.cognito_id = params['cognito_id'];
      let title = document.getElementById('group_blocks_title');
      if (title)
        title.textContent = `Comparison Scenarios for Grower: ${this.group}`;
      //this.loadFarms();
    });
    this.loadGrowers();
    this.auth.getUser().then((user: any) => {
      //if no user was found sent to login again
      this.isGrower = user.attributes['custom:user_type'] == 'GROWER';
    });
  }

  loadAssignedBlocks(group: string, farm: string) {
    this.groupApi.getAssignedBlocks(group, farm).subscribe((l) => {
      this.logging.info(l.body)
      if (l.body) {
        l.body.forEach((i: any) => {
          let farms = this.growersData.find(
            (obj: any) => obj.cognito_id == this.cognito_id
          ).farms;
          let farm_local = farms.filter((f: any) => f.id == farm)[0];
          let block = farm_local.blocks.find(
            (block: any) => block.block_id == i.id
          );

          
          if (block){
            this.availableBlocks.push({
              id: i.id,
              block_number: i.block_number,
              area: i.area,
              default_yield: block.default_yield,
              topping: block.topping,
              moisture: block.field_moisture,
              ccs: block.default_ccs != null ? block.default_ccs * 100 : undefined,
              default_ground_speed: block.default_ground_speed,
              default_fan_speed: block.default_fan_speed,
              harvester_ext_id: i.harvester_ext_id ?? null,
              harvester_model: i.harvester_model ?? { row_type: null },
              // Carry the crop's cane_type through to the calc payload so
              // block_results can send it (previously dropped → always null).
              cane_type: block.cane_type ?? null,
            });
          }

        });
      }
    });
  }

  onSelectFarm(farm: any) {
    this.assignedBlocks = [];
    this.availableBlocks = [];
    let farm_ext_id = farm.value;
    this.selectedFarm = farm.value;
    // SRA003-454: the "Select Farm" placeholder has no value — skip the
    // loader rather than fire GET /group/{id}/farms/Select%20Farm/blocks.
    if (!isUsableId(farm_ext_id) || !isUsableId(this.group_id)) {
      return;
    }
    this.loadAssignedBlocks(this.group_id, farm_ext_id);
  }

  onSelectSeason(season: any) {
    this.season = season.value;
  }

  removeBlocks(block?: any) {
    let payload: any = { blocks: [], farm_id: this.selectedFarm };
    this.total_representative_area = 0;

    if (block != undefined) {
      payload.blocks.push(block.block_number);
      this.save_results_remove(block.block_number);

      this.assignedBlocks.forEach((b: any) => {

        if (b.block_number == block.block_number) {
          b.percent_area = 0;
          b.block_representative_area = 0;
        } else {

          //this.save_results(b)//update current selected blocks for save results API
        }
      });

      this.assignedBlocks.forEach((f: any) => {
        this.total_representative_area += parseFloat(
          f.block_representative_area
        );
      });

      this.assignedBlocks.forEach((f: any) => {
        f.percent_area =
          f.block_representative_area / this.total_representative_area;
      });
    } else {
      this.assignedBlocks.forEach((block: any) => {
        block.percent_area = 0;
        block.total_repesentative_area = 0;

        this.assignedBlocks.forEach((f: any) => {
          this.total_representative_area += parseFloat(
            f.block_representative_area
          );
        });

        this.assignedBlocks.forEach((f: any) => {
          f.percent_area =
            f.block_representative_area / this.total_representative_area;
        });

        payload.blocks.push(block.block_number);
      });
    }
  }

  /**
   * SRA003-408: predicted scenarios + the standard row that survive the
   * current "Max working hours/day" filter. Reactive — recomputed from the
   * already-returned `block_results` whenever `maxWorkingHours` changes; no BE
   * call. Delegates to the pure `filterScenariosByMaxHours`.
   */
  get displayedResults(): any[] {
    return filterScenariosByMaxHours(this.block_results, this.maxWorkingHours);
  }

  /**
   * True when every predicted scenario exceeds the threshold and the filter
   * fell back to showing all of them — drives the subtle "showing all" note.
   */
  get isHoursFilterFallback(): boolean {
    const predicted = (this.block_results ?? []).filter(
      (r: any) => !r?.standard,
    );
    return (
      predicted.length > 0 &&
      !predicted.some(
        (r: any) =>
          (r?.block_agronomic_results?.hours_per_day_total_pred ?? Infinity) <=
          this.maxWorkingHours,
      )
    );
  }

  /**
   * SRA003-468/-469: re-gate the already-returned sweep when the "Show optimal
   * scenarios only" toggle changes — no BE call (mirrors the -408 hours
   * filter's reactivity). No-op until a calculation has run.
   */
  onOptimalFilterToggle(): void {
    if (!this.stdResult) return;
    this.block_results = [
      this.stdResult,
      ...filterScenariosByOptimal(this.rawCalcResults, this.showOptimalOnly),
    ];
  }

  /**
   * SRA003-468/-469: true when the is_optimal gate left no predicted scenario —
   * only Standard Practice renders. Drives the subtle note suggesting 0 for the
   * full sweep. Mutually exclusive with the -408 hours note, which needs at
   * least one predicted row.
   */
  get isBenefitTrimEmpty(): boolean {
    return (
      this.rawCalcResults.length > 0 &&
      !this.block_results.some((r: any) => !r?.standard)
    );
  }

  /** Whether a scenario row is visible under the current hours filter. */
  isScenarioShown(result: any): boolean {
    if (result?.standard) return true;
    return this.displayedResults.includes(result);
  }

  selectThisResult(result: any, index: any) {
    this.save_results_add(result, this.block);
    
    
    this.representative_areas.forEach((f: any) => {
      if (f.block_number == this.block) {
        f.block_results = result;
      }
    });
    
    this.assignedBlocks.forEach((block: any) => {
      if (block.block_number == this.block) {
        result.ranking = index
        block.results = this.block_results;
        block.selected_result = result
      }
    });

    this.dialog_open = false;
  }

  drop(event: any) {
    if (event.previousContainer == event.container) {
      moveItemInArray(
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    } else {
      if (event.previousContainer.id == 'assignedBlocksList')
        this.removeBlocks(event.previousContainer.data[event.previousIndex]);

      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );

      if (event.previousContainer.id == 'availableBlocksList') {
        const droppedBlock = event.container.data[event.currentIndex];

        this.payload = {
          group_id: this.group_id,
          farm_id: this.selectedFarm,
          block_number: droppedBlock.block_number,
          moisture: droppedBlock.moisture,
          std_ccs:
            typeof droppedBlock.ccs === 'number'
              ? droppedBlock.ccs / 100
              : null,
          std_topping: droppedBlock.topping,
          std_ground_speed: droppedBlock.default_ground_speed,
          std_fan_speed: droppedBlock.default_fan_speed,
          cane_type: droppedBlock.cane_type || null,
        };

        this.allowance_open = true;
        this.block = droppedBlock.block_number;
      }
    }

    //set default representative area & yield estimate
    this.block_allowance.total_repesentative_area =
      event.container.data[event.currentIndex].area;
    this.block_allowance.delivered_yield_estimate =
      event.container.data[event.currentIndex].default_yield * this.block_allowance.total_repesentative_area;
  }
}
